/**
 * ============================================================================
 * ETCHED REFLECTION - Custom Order Form Handler (3-Step Process)
 * ============================================================================
 *
 * Features:
 * - 3-step simplified form navigation
 * - Visual product card selection with pricing
 * - Real-time price calculator
 * - Image upload with previews
 * - Auto-fill form data
 * - Form validation and AJAX submission
 *
 * ============================================================================
 */

(function() {
  'use strict';

  const CustomOrderForm = {
    // Configuration
    config: {
      totalSections: 3,
      maxFileSize: 10 * 1024 * 1024, // 10MB
      allowedFileTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
    },

    // State
    state: {
      currentSection: 1,
      completedSections: new Set(),
      selectedProduct: null,
      isSubmitting: false
    },

    // DOM Elements
    elements: {
      form: null,
      sections: [],
      progressBar: null,
      progressText: null,
      progressFill: null,
      productCards: null,
      submitButton: null
    },

    // Product pricing data - Updated to match exact pricing
    products: {
      'broadway-clip-on': { name: 'Broadway Clip-On Mirror', price: 140, supplier: 'us' },
      'oem-rearview': { name: 'OEM Rear View Mirror', price: 120, supplier: 'you' },
      'oem-side-x1': { name: 'OEM Side Mirror x1', price: 95, supplier: 'you' },
      'oem-side-x2': { name: 'OEM Side Mirrors x2', price: 160, supplier: 'you' },
      'full-set': { name: 'Full Mirror Set', price: 250, supplier: 'you' }
    },

    init() {
      // Check if form was successfully submitted via URL parameter
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('contact_posted') === 'true') {
        this.showSuccessMessage();
        console.log('Form already submitted - showing success message');
        return;
      }

      this.elements.form = document.querySelector('.custom-order-form');
      if (!this.elements.form) return;

      this.cacheElements();
      this.bindEvents();
      this.updateProgress();
      this.showSection(1);
      this.initializeAutoFill();

      console.log('Custom Order Form initialized');
    },

    showSuccessMessage() {
      // Find the form container
      const container = document.querySelector('.custom-order-section .container');
      if (!container) return;

      // Hide the form and progress bar
      const form = container.querySelector('.custom-order-form');
      const progressBar = container.querySelector('.form-progress');
      const sectionHeader = container.querySelector('.section__header');
      
      if (form) form.style.display = 'none';
      if (progressBar) progressBar.style.display = 'none';

      // Create and inject success message
      const successHTML = `
        <div class="form-success-message" style="background: #10b981; color: white; padding: 2rem; border-radius: 8px; text-align: center; margin-bottom: 2rem;">
          <svg style="width: 48px; height: 48px; margin: 0 auto 1rem; display: block;" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <h2 style="font-size: 1.5rem; font-weight: 700; margin-bottom: 0.5rem;">Order Request Submitted Successfully!</h2>
          <p style="font-size: 1rem; margin-bottom: 0.5rem;">Thank you for your custom order inquiry. We've received your request and will be in touch within 24-48 hours.</p>
          <p style="font-size: 0.875rem; opacity: 0.9;">Your order details have been sent to our team at <strong>etched.reflection@gmail.com</strong></p>
          <a href="/pages/custom-orders" style="display: inline-block; margin-top: 1rem; padding: 0.75rem 1.5rem; background: white; color: #10b981; text-decoration: none; border-radius: 4px; font-weight: 600;">Submit Another Order</a>
        </div>
      `;

      // Insert after section header or at the beginning
      if (sectionHeader) {
        sectionHeader.insertAdjacentHTML('afterend', successHTML);
      } else {
        container.insertAdjacentHTML('afterbegin', successHTML);
      }
    },

    cacheElements() {
      this.elements.sections = Array.from(
        document.querySelectorAll('.collapsible[data-section]')
      );
      this.elements.progressBar = document.querySelector('.form-progress');
      this.elements.progressText = document.querySelector('.form-progress__text');
      this.elements.progressFill = document.querySelector('.form-progress__fill');
      this.elements.productCards = document.querySelectorAll('.product-card');
      this.elements.submitButton = document.querySelector('.form-submit-btn');
    },

    bindEvents() {
      // Section navigation
      this.elements.sections.forEach(section => {
        const header = section.querySelector('.collapsible__header');
        if (header) {
          header.addEventListener('click', (e) => {
            e.preventDefault();
            const sectionNum = parseInt(section.dataset.section, 10);
            this.goToSection(sectionNum);
          });
        }
      });

      // Navigation buttons
      document.querySelectorAll('[data-nav-next]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          if (this.validateCurrentSection()) {
            this.nextSection();
          }
        });
      });

      document.querySelectorAll('[data-nav-prev]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.prevSection();
        });
      });

      // Product card selection
      this.elements.productCards.forEach(card => {
        const input = card.querySelector('.product-card__input');
        if (input) {
          input.addEventListener('change', () => {
            this.handleProductSelection(input.value);
          });
        }
      });

      // File uploads removed - using URL input instead (Shopify contact form doesn't support file uploads)

      // Form submission - ONLY prevent if invalid, otherwise do NOTHING
      this.elements.form.addEventListener('submit', (e) => {
        // Validate all sections
        let allValid = true;
        for (let i = 1; i <= this.config.totalSections; i++) {
          this.state.currentSection = i;
          if (!this.validateCurrentSection()) {
            allValid = false;
            this.showSection(i);
            e.preventDefault(); // ONLY prevent if validation fails
            return false;
          }
        }
        
        // If valid: do absolutely nothing - let browser POST naturally
      });

      // Real-time validation
      this.elements.form.querySelectorAll('input, select, textarea').forEach(input => {
        input.addEventListener('blur', () => {
          this.validateField(input);
        });

        input.addEventListener('input', () => {
          if (input.classList.contains('is-error')) {
            this.validateField(input);
          }
        });
      });
    },

    // Section Management
    showSection(num) {
      this.state.currentSection = num;

      this.elements.sections.forEach(section => {
        const sectionNum = parseInt(section.dataset.section, 10);
        const isActive = sectionNum === num;

        section.classList.toggle('is-active', isActive);
        section.classList.toggle('is-complete', this.state.completedSections.has(sectionNum));
      });

      this.updateProgress();
      this.scrollToSection(num);

      // Update price summary when on step 3
      if (num === 3) {
        this.updatePriceSummary();
      }
    },

    goToSection(num) {
      // Allow going back freely, but validate going forward
      // Skip validation in Shopify design mode for testing
      const isDesignMode = window.Shopify && window.Shopify.designMode;
      
      if (num > this.state.currentSection && !isDesignMode) {
        if (!this.validateCurrentSection()) {
          return;
        }
        this.state.completedSections.add(this.state.currentSection);
      } else if (isDesignMode) {
        // In design mode, allow free navigation for testing
        this.state.completedSections.add(this.state.currentSection);
      }

      this.showSection(num);
    },

    nextSection() {
      if (this.state.currentSection < this.config.totalSections) {
        this.state.completedSections.add(this.state.currentSection);
        this.showSection(this.state.currentSection + 1);
      }
    },

    prevSection() {
      if (this.state.currentSection > 1) {
        this.showSection(this.state.currentSection - 1);
      }
    },

    scrollToSection(num) {
      const section = document.querySelector(`[data-section="${num}"]`);
      if (!section) return;

      const headerHeight = document.querySelector('.site-header')?.offsetHeight || 80;
      const progressHeight = this.elements.progressBar?.offsetHeight || 0;
      const offset = headerHeight + progressHeight + 20;

      const targetPosition = section.getBoundingClientRect().top + window.scrollY - offset;

      const currentScroll = window.scrollY;
      if (Math.abs(targetPosition - currentScroll) > 100) {
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
      }
    },

    // Progress Tracking
    updateProgress() {
      const progress = ((this.state.currentSection - 1) / (this.config.totalSections - 1)) * 100;

      if (this.elements.progressFill) {
        this.elements.progressFill.style.width = `${progress}%`;
      }

      if (this.elements.progressText) {
        this.elements.progressText.innerHTML = `<span>${this.state.currentSection}</span>/${this.config.totalSections}`;
      }
    },

    // Product Selection
    handleProductSelection(value) {
      this.state.selectedProduct = value;
      this.updatePriceSummary();
      
      // Update visual state
      this.elements.productCards.forEach(card => {
        const input = card.querySelector('.product-card__input');
        card.classList.toggle('is-selected', input.value === value && input.checked);
      });
    },

    // Price Summary
    updatePriceSummary() {
      const selectedInput = document.querySelector('.product-card__input:checked');
      let productName = 'No product selected';
      let basePrice = 0;

      if (selectedInput) {
        const productId = selectedInput.value;
        const product = this.products[productId];
        if (product) {
          productName = product.name;
          basePrice = product.price;
        }
      }

      // Update summary in Step 3
      const productLabel = document.querySelector('[data-summary-product]');
      const priceValue = document.querySelector('[data-summary-price]');
      const totalValue = document.querySelector('[data-summary-total]');

      if (productLabel) {
        productLabel.textContent = productName;
      }

      if (priceValue) {
        priceValue.textContent = `£${basePrice}`;
      }

      if (totalValue) {
        totalValue.textContent = `£${basePrice} + shipping`;
      }
    },

    // File upload methods removed - using URL input instead

    // Auto-fill functionality
    initializeAutoFill() {
      // Try to get saved data from localStorage
      const savedData = localStorage.getItem('etchedReflection_customerData');
      if (savedData) {
        try {
          const data = JSON.parse(savedData);
          this.populateForm(data);
        } catch (e) {
          console.error('Error loading saved data:', e);
        }
      }

      // Save on blur for relevant fields
      const saveFields = ['contact-name', 'contact-email', 'contact-phone', 'shipping-address', 'shipping-city', 'shipping-postcode', 'shipping-country'];
      saveFields.forEach(fieldId => {
        const field = document.getElementById(fieldId);
        if (field) {
          field.addEventListener('blur', () => {
            this.saveFormData();
          });
        }
      });
    },

    populateForm(data) {
      Object.keys(data).forEach(key => {
        const field = document.getElementById(key);
        if (field && !field.value) {
          field.value = data[key];
        }
      });
    },

    saveFormData() {
      const data = {
        'contact-name': document.getElementById('contact-name')?.value || '',
        'contact-email': document.getElementById('contact-email')?.value || '',
        'contact-phone': document.getElementById('contact-phone')?.value || '',
        'shipping-address': document.getElementById('shipping-address')?.value || '',
        'shipping-city': document.getElementById('shipping-city')?.value || '',
        'shipping-postcode': document.getElementById('shipping-postcode')?.value || '',
        'shipping-country': document.getElementById('shipping-country')?.value || ''
      };

      const saveCheckbox = document.querySelector('input[name="contact[save_details]"]');
      if (saveCheckbox && saveCheckbox.checked) {
        localStorage.setItem('etchedReflection_customerData', JSON.stringify(data));
      }
    },

    // Validation
    validateCurrentSection() {
      const section = document.querySelector(`[data-section="${this.state.currentSection}"]`);
      if (!section) return true;

      const requiredFields = section.querySelectorAll('[required]');
      let isValid = true;

      requiredFields.forEach(field => {
        if (!this.validateField(field)) {
          isValid = false;
        }
      });

      return isValid;
    },

    validateField(field) {
      const value = field.value.trim();
      const type = field.type;
      const name = field.name;
      let error = '';

      // Required check
      if (field.required && !value) {
        error = 'This field is required';
      }

      // Email validation
      else if (type === 'email' && value) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          error = 'Please enter a valid email address';
        }
      }

      // Phone validation (basic)
      else if (type === 'tel' && value) {
        const phoneRegex = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/;
        if (!phoneRegex.test(value.replace(/\s/g, ''))) {
          error = 'Please enter a valid phone number';
        }
      }

      // Instagram handle validation
      else if (name && name.includes('instagram') && value) {
        const handle = value.replace(/^@/, '');
        const instagramRegex = /^[a-zA-Z0-9._]{1,30}$/;
        if (!instagramRegex.test(handle)) {
          error = 'Please enter a valid Instagram handle';
        }
      }

      if (error) {
        this.showFieldError(field, error);
        return false;
      } else {
        this.clearFieldError(field);
        return true;
      }
    },

    showFieldError(field, message) {
      field.classList.add('is-error');

      const existingError = field.parentNode.querySelector('.form-error');
      if (existingError) {
        existingError.textContent = message;
        return;
      }

      const errorEl = document.createElement('div');
      errorEl.className = 'form-error';
      errorEl.textContent = message;
      field.parentNode.appendChild(errorEl);
    },

    clearFieldError(field) {
      field.classList.remove('is-error');
      const errorEl = field.parentNode.querySelector('.form-error');
      if (errorEl) {
        errorEl.remove();
      }
    },

    // Form submission is now handled naturally by Shopify - no custom submit handler needed

    showError(message) {
      const existingError = this.elements.form.querySelector('.message--error');
      if (existingError) {
        existingError.textContent = message;
        return;
      }

      const errorEl = document.createElement('div');
      errorEl.className = 'message message--error';
      errorEl.textContent = message;
      this.elements.form.insertBefore(errorEl, this.elements.form.firstChild);

      errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // Initialize on DOMContentLoaded
  document.addEventListener('DOMContentLoaded', () => {
    CustomOrderForm.init();
  });

  // Expose for external use
  window.EtchedReflection = window.EtchedReflection || {};
  window.EtchedReflection.CustomOrderForm = CustomOrderForm;

})();
